// App.jsx
// Only the auth-related wiring is shown here.
// Replace the auth section in your existing App.jsx with this pattern.
// Everything else (cart, routes) stays the same.

import { useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Navbar from './pages/Navbar';
import WriteReview from './components/WriteReview';
import Home from './pages/Home';
import ServiceDetailRouter from './pages/ServiceDetailRouter';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import PortfolioPage from './pages/PortfolioPage';
import TestimonialsPage from './pages/TestimonialsPage';
import ProductsPage from './pages/ProductsPage';
import ShopPage from './pages/ShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import WishlistPage from './pages/WishlistPage';
import { CartDrawer } from './pages/CartDrawer';
import { AuthModal } from './pages/AuthModal';
import { useAuthContext } from './hooks/AuthContext';
import AccountPage from './pages/AccountPage';
import CheckoutPage from "./pages/CheckoutPage";

/* ── Cart helpers ── */
function loadCart() {
  try { return JSON.parse(localStorage.getItem('cart') || '[]'); } catch { return []; }
}
function saveCart(items) {
  localStorage.setItem('cart', JSON.stringify(items));
}

function App() {
  /* ── Cart state ── */
  const [cartItems, setCartItems] = useState(loadCart);
  const [cartOpen,  setCartOpen]  = useState(false);

  /* ── Auth — single source of truth from context ── */
  const { isLoggedIn, customer, login, logout } = useAuthContext();

  /* ── Auth modal ── */
  const [authModal, setAuthModal] = useState({ open: false, tab: 'login' });
  const openAuth  = useCallback((tab = 'login') => setAuthModal({ open: true, tab }), []);
  const closeAuth = useCallback(() => setAuthModal((s) => ({ ...s, open: false })), []);

  /* ── Cart handlers ── */
  const handleAddToCart = useCallback((product) => {
    setCartItems((prev) => {
      const qty    = product.qty ?? 1;
      const exists = prev.find((i) => i.id === product.id);
      const next   = exists
        ? prev.map((i) => i.id === product.id ? { ...i, qty: Math.min(i.qty + qty, product.quantity ?? 999) } : i)
        : [...prev, { ...product, qty }];
      saveCart(next);
      return next;
    });
    setCartOpen(true);
  }, []);

  const handleRemoveFromCart = useCallback((productId) => {
    setCartItems((prev) => { const next = prev.filter((i) => i.id !== productId); saveCart(next); return next; });
  }, []);

  const handleUpdateQty = useCallback((productId, qty) => {
    setCartItems((prev) => {
      const next = qty < 1 ? prev.filter((i) => i.id !== productId) : prev.map((i) => i.id === productId ? { ...i, qty } : i);
      saveCart(next);
      return next;
    });
  }, []);

  const handleClearCart = useCallback(() => { setCartItems([]); saveCart([]); }, []);

  const cartProps = {
    cartItems,
    onAddToCart:      handleAddToCart,
    onRemoveFromCart: handleRemoveFromCart,
    onUpdateQty:      handleUpdateQty,
    onClearCart:      handleClearCart,
  };

  return (
    <BrowserRouter>
      <Navbar
        {...cartProps}
        isLoggedIn={isLoggedIn}
        customer={customer}
        onAuthLogin={login}
        onLogout={logout}
        onOpenCart={() => setCartOpen(true)}
        onOpenAuth={openAuth}
      />
      <WriteReview />

      <Routes>
        <Route path="/checkout" element={
          <CheckoutPage cartItems={cartItems} onClearCart={handleClearCart} />
        } />
        <Route path="/"               element={<Home />} />
        <Route path="/services/:slug" element={<ServiceDetailRouter />} />
        <Route path="/about-us"       element={<AboutPage />} />
        <Route path="/contact-us"     element={<ContactPage />} />
        <Route path="/blog"           element={<BlogPage />} />
        <Route path="/blog/:slug"     element={<BlogDetailPage />} />
        <Route path="/testimonials"   element={<TestimonialsPage />} />
        <Route path="/portfolio"      element={<PortfolioPage />} />
        <Route path="/products"       element={<ProductsPage />} />
        <Route path="/account"        element={<AccountPage onAddToCart={handleAddToCart} />} />
        <Route path="/shop"           element={
          <ShopPage
            {...cartProps}
            isLoggedIn={isLoggedIn}
            customer={customer}
            onLogout={logout}
            onOpenCart={() => setCartOpen(true)}
            onOpenAuth={openAuth}
          />
        } />
        <Route path="/shop/:slug"  element={<ProductDetailPage onAddToCart={handleAddToCart} />} />
        <Route path="/wishlist"    element={<WishlistPage onAddToCart={handleAddToCart} />} />
        
      </Routes>

      <CartDrawer
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        {...cartProps}
        isLoggedIn={isLoggedIn}
        onOpenAuth={openAuth}
      />

      {/* AuthModal receives login directly from context — no wrapper */}
      <AuthModal
        isOpen={authModal.open}
        initialTab={authModal.tab}
        onClose={closeAuth}
        onLogin={login}
      />
    </BrowserRouter>
  );
}

export default App;